import * as api from 'api/endpoints/tenantUsers/tenantUsers.api';
import * as TenantUsersTypes from './tenantUsers.types';
import { brandUsersError } from 'utils/errorCodes';
import { notificationErr } from 'views/components/notification';
import { messageError, messageSuccess, messageWarning } from 'views/components/message';
import {
  ITenantUser,
  IcreateUpdateTenantUserProps,
} from 'interfaces/tenantUsers.interface';
import { superAdminRole } from 'utils/constants';
import { ITranslate } from 'interfaces/general.interface';
import { TtenantUsersDispatchReducer } from './tenantUsers.reducer';
import { addNotificationsTypeToUserAPI } from 'api/endpoints/notifications/notifications.api';
import {
  IcreateTenantUserAPIBody,
  IupdateTenantUserAPIBody,
} from 'api/endpoints/tenantUsers/tenantUsers.response.interface';

export async function getTenantUsersAction({
  dispatch,
  tenantId,
  userRole,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TtenantUsersDispatchReducer>;
  tenantId: string;
  userRole: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    const brandusersFetch = await api.getTenantUsersAPI({ tenantId, token });
    if (brandusersFetch.response.status === 200) {
      const orderedTenantUsers: Array<ITenantUser> = [];
      brandusersFetch.data.forEach((item) => {
        if (
          userRole !== superAdminRole &&
          item.username.split('@')[1] === 'hub4retail.com'
        )
          return;
        const obj: ITenantUser = {
          ...item,
          key: item.id,
          tenantId,
        };
        orderedTenantUsers.push(obj);
      });
      dispatch({
        type: TenantUsersTypes.GET_BRAND_USERS,
        payload: orderedTenantUsers,
      });
      return true;
    }
    return false;
  } catch (err) {
    messageError(translate('actions_brand-users_get-brand-users_error'));
    notificationErr(brandUsersError.get_brand_users_001, translate);
    return false;
  }
}

export function selectTenantUserAction({
  dispatch,
  user,
}: {
  dispatch: React.Dispatch<TtenantUsersDispatchReducer>;
  user: ITenantUser | null;
}) {
  return dispatch({
    type: TenantUsersTypes.SELECT_BRAND_USER,
    payload: user,
  });
}

export async function createTenantUserAction({
  dispatch,
  tenantId,
  newTenantUser,
  token,
  translate,
  addUserClient,
}: {
  dispatch: React.Dispatch<TtenantUsersDispatchReducer>;
  tenantId: string;
  newTenantUser: IcreateUpdateTenantUserProps;
  token: string;
  translate: ITranslate;
  addUserClient: (clientId: string, user: ITenantUser) => void;
}): Promise<boolean> {
  dispatch({ type: TenantUsersTypes.SET_IS_LOADING_ACTION, payload: true });
  try {
    const body: IcreateTenantUserAPIBody = {
      user_data: {
        email: newTenantUser.email,
        name: newTenantUser.name,
        nickname: newTenantUser.surname,
        verify_email: true,
        email_verified: true,
        blocked: false,
      },
      user_metadata: {
        tenant_id: tenantId,
        telephone_sufix: '',
        telephone_prefix: '',
        language: newTenantUser.i18n_lang_id,
        sector: newTenantUser.sector,
      },
      role: newTenantUser.role,
    };
    const createFetch = await api.createTenantUserAPI({ body, token });
    if (createFetch.response.status === 409) {
      return messageWarning(translate('actions_brand-users_create-brand-user_exists'));
    }
    if (createFetch.response.status === 201 || createFetch.response.status === 200) {
      const userCreated: ITenantUser = {
        ...createFetch.data.database.db_user,
        key: createFetch.data.database.db_user.id,
        tenantId: tenantId,
        i18n_lang: {
          id: '',
          code: '',
          name: '',
        },
        user_notifications: [],
      };
      addUserClient(tenantId, userCreated);
      dispatch({
        type: TenantUsersTypes.CREATE_BRAND_USER,
        payload: userCreated,
      });
      messageSuccess(translate('actions_brand-users_create-brand-user_success'));
      return true;
    } else {
      messageError(translate('actions_brand-users_create-brand-user_error'));
      notificationErr(brandUsersError.create_brand_user_002, translate);
      return false;
    }
  } catch (err) {
    messageError(translate('actions_brand-users_create-brand-user_error'));
    notificationErr(brandUsersError.create_brand_user_001, translate);
    return false;
  } finally {
    dispatch({ type: TenantUsersTypes.SET_IS_LOADING_ACTION, payload: false });
  }
}

export async function updateTenantUserAction({
  dispatch,
  user,
  userId,
  updateRole,
  prevRole,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TtenantUsersDispatchReducer>;
  user: IcreateUpdateTenantUserProps;
  userId: string;
  updateRole: boolean;
  prevRole: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  dispatch({ type: TenantUsersTypes.SET_IS_LOADING_ACTION, payload: true });
  if (updateRole) {
    try {
      // Update role
      const updateRoleFetch = await api.updateRoleAPI({
        user_id: userId,
        role: user.role,
        prev_role: prevRole,
        token,
      });
      if (updateRoleFetch.response.status !== 200) {
        messageError(translate('actions_brand-users_update-brand-user_rol-error'));
        notificationErr(brandUsersError.update_brand_user_003, translate);
      }
    } catch (err) {
      messageError(translate('actions_brand-users_update-brand-user_rol-error'));
      notificationErr(brandUsersError.update_brand_user_004, translate);
    }
  }
  try {
    const body: IupdateTenantUserAPIBody = {
      filter: { id: userId },
      update: {
        name: user.name,
        surname: user.surname,
        sector: user.sector,
        i18n_lang_id: user.i18n_lang_id,
      },
    };
    const updateFetch = await api.updateMainContactAPI({ body, token });
    if (updateFetch.response.status === 200) {
      try {
        const addNotificationsTypes = await addNotificationsTypeToUserAPI({
          userId,
          notificationsTypesIds: user.notifications,
          token,
        });
        if (
          addNotificationsTypes.response.status === 200 ||
          addNotificationsTypes.response.status === 201
        ) {
          dispatch({
            type: TenantUsersTypes.UPDATE_BRAND_USER,
            payload: {
              key: userId,
              user,
              notifications: Array.isArray(addNotificationsTypes.data)
                ? addNotificationsTypes.data
                : [],
            },
          });
          messageSuccess(translate('actions_brand-users_update-brand-user_success'));
          return true;
        }
        messageError(
          translate('actions_brand-users_update-brand-user_notifications-error')
        );
        notificationErr(brandUsersError.update_brand_user_006, translate);
        return false;
      } catch (error) {
        messageError(translate('actions_brand-users_update-brand-user_error'));
        notificationErr(brandUsersError.update_brand_user_005, translate);
        return false;
      }
    } else {
      messageError(translate('actions_brand-users_update-brand-user_error'));
      notificationErr(brandUsersError.update_brand_user_002, translate);
      return false;
    }
  } catch (err) {
    messageError(translate('actions_brand-users_update-brand-user_error'));
    notificationErr(brandUsersError.update_brand_user_001, translate);
    return false;
  } finally {
    dispatch({ type: TenantUsersTypes.SET_IS_LOADING_ACTION, payload: false });
  }
}

export async function deleteTenantUserAction({
  dispatch,
  userId,
  token,
  translate,
}: {
  dispatch: React.Dispatch<TtenantUsersDispatchReducer>;
  userId: string;
  token: string;
  translate: ITranslate;
}): Promise<boolean> {
  try {
    const updateFetch = await api.deleteTenantUserAPI({ userId, token });
    if (updateFetch.response.status === 200) {
      dispatch({
        type: TenantUsersTypes.DELETE_BRAND_USER,
        payload: userId,
      });
      messageSuccess(translate('actions_brand-users_delete-brand-user_success'));
      return true;
    }
    messageError(translate('actions_brand-users_delete-brand-user_error'));
    notificationErr(brandUsersError.update_brand_user_002, translate);
    return false;
  } catch (err) {
    messageError(translate('actions_brand-users_delete-brand-user_error'));
    notificationErr(brandUsersError.update_brand_user_001, translate);
    return false;
  }
}
